import React, { ReactNode } from 'react';

import {
  FONT_WEIGHTS,
  GAP_SCALE,
  SPACE_Y_SCALE,
  SURFACE_PADDING,
  TYPE_SCALE,
  RADIUS_SCALE,
  PADDING_X,
  PADDING_Y,
  COLOR_SCALE,
} from '@/components/design-system/tokens';
import { useTranslation } from '@/hooks/useTranslation';

import ErrorBoundary from './ErrorBoundary';

import type { ErrorBoundaryProps } from './ErrorBoundary';

interface InlineSectionFallbackProps {
  error: Error | null;
  lastErrorAt: string | null;
  resetError: () => void;
  fallbackTitle?: string;
  fallbackMessage?: string;
}

interface SectionErrorBoundaryProps {
  source?: string;
  children: ReactNode;
  fallbackTitle?: string;
  fallbackMessage?: string;
  renderFallback?: (props: InlineSectionFallbackProps) => ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  onReset?: () => void;
  resetKeys?: unknown;
}

function InlineSectionFallback({
  error,
  lastErrorAt,
  resetError,
  fallbackTitle,
  fallbackMessage,
}: InlineSectionFallbackProps) {
  const { t } = useTranslation();
  const title =
    fallbackTitle ?? t('error_boundary', 'section_error_title', 'Content temporarily unavailable');
  const message =
    fallbackMessage ??
    t(
      'error_boundary',
      'section_error_message',
      'We could not load this section. Please try again.'
    );

  return (
    <div
      className={`w-full ${RADIUS_SCALE.xl} border border-destructive/20 bg-destructive/5 ${SURFACE_PADDING['4']} text-sm text-destructive shadow-sm`}
    >
      <div className={`flex items-start justify-between ${GAP_SCALE['3']}`}>
        <div className={SPACE_Y_SCALE['1']}>
          <p className={`${FONT_WEIGHTS.semibold}`}>{title}</p>
          <p className={`${COLOR_SCALE.destructive}/80`}>{message}</p>
          <p className={`${TYPE_SCALE.xs} text-destructive/70`}>
            {t('error_boundary', 'timestamp_label', 'Timestamp')}:{' '}
            {lastErrorAt ?? new Date().toISOString()}
          </p>
          {error?.message && (
            <p className={`${TYPE_SCALE.xs} text-destructive/60`}>
              {t('error_boundary', 'error_message_label', 'Error')}: {error.message}
            </p>
          )}
        </div>
        <button
          onClick={resetError}
          className={`${RADIUS_SCALE.md} border border-destructive/30 bg-background/80 ${PADDING_X['3']} ${PADDING_Y['1.5']} text-xs ${FONT_WEIGHTS.medium} text-destructive transition hover:bg-background`}
        >
          {t('error_boundary', 'retry_section', 'Retry section')}
        </button>
      </div>
    </div>
  );
}

export default function SectionErrorBoundary({
  source,
  children,
  fallbackTitle,
  fallbackMessage,
  renderFallback,
  onError,
  onReset,
  resetKeys,
}: SectionErrorBoundaryProps) {
  const inlineFallbackRender =
    renderFallback ??
    (props => (
      <InlineSectionFallback
        {...props}
        {...(fallbackTitle && { fallbackTitle })}
        {...(fallbackMessage && { fallbackMessage })}
      />
    ));

  const errorBoundaryProps: Partial<ErrorBoundaryProps> = {
    fallbackRender: inlineFallbackRender,
    contextMetadata: { boundaryType: 'section', ...(source && { source }) },
    resetKeys,
  };

  if (source) errorBoundaryProps.source = source;
  if (onError) errorBoundaryProps.onError = onError;
  if (onReset) errorBoundaryProps.onReset = onReset;

  return <ErrorBoundary {...errorBoundaryProps}>{children}</ErrorBoundary>;
}
